/**
 * RaceTrace Live: the Strategy tab. For the focus car, its pit window, where it rejoins if it
 * stops now (and what a stop under the current caution saves), and its projected finish; for its
 * class, stops still to make, the order once every car has made them, and the projected order at
 * the flag. All from live-strategy.ts, rebuilt on each update.
 */
import { lapAhead, lapsAhead } from "./lap-ahead";
import { useMemo, type ReactNode } from "react";
import type { AnnotationData, RaceChartData } from "@shared/types";
import { carNo } from "./car-number";
import { gapClock, lapsAndSeconds } from "./live-gaps";
import {
  fuelSaveOutcome,
  liveStrategy,
  reachToFlag,
  settledText,
  pitTrendArrow,
  pitTrendText,
  closeCall,
  stopsEdge,
  type AtFlag,
  type CarStrategy,
  type CautionRates,
  type LastHere,
  lastHereClock,
  lastHereText,
  type RaceHistory,
  type LiveStrategy,
  type ReachToFlag,
  type PitNow,
  cautionLengthNote,
  WRL_CAUTION_RATES,
  WEATHERTECH_CAUTION_RATES,
  PILOT_CHALLENGE_CAUTION_RATES,
} from "./live-strategy";
import { liveBattles, recentPace, type Battle } from "./live-battles";
import { finishRange, finishRangeBandsFor, type FinishRange } from "./finish-range";
import { lapsDown } from "./laps-down";
import { useIsCompact, useIsLandscapePhone } from "../../lib/breakpoint";
import { RankedMetrics, type RankedItem, type RankedMetric } from "./RankedMetrics";

const T = { line: "#1e1e38", card: "#12122a", text: "#e0e0e8", soft: "#cbd5e1", muted: "#9a9ab4", accent: "#F48C34", gain: "#4ade80", lose: "#f87171", caution: "#facc15" };

/** Behind the class leader: "54.1 s", or "12.3 (+1L)" at the leader's pace. */
function behind(sec: number | null, pace: number | null): string {
  if (sec === null) return "—";
  if (sec < 0.05) return "Leader";
  return pace ? lapsAndSeconds(sec / pace, pace) : gapClock(sec);
}

function Move({ from, to }: { from: number; to: number | null }) {
  if (to === null) return <span style={{ color: T.muted }}>—</span>;
  const d = from - to;
  return (
    <span className="whitespace-nowrap">
      P{to}
      {d !== 0 && (
        <span className="ml-1 text-xs" style={{ color: d > 0 ? T.gain : T.lose }} aria-label={d > 0 ? `up ${d}` : `down ${-d}`}>
          {d > 0 ? "▲" : "▼"}
          {Math.abs(d)}
        </span>
      )}
    </span>
  );
}

const Note = ({ children }: { children: ReactNode }) => <p className="m-0 text-[13px] leading-snug" style={{ color: T.soft }}>{children}</p>;

/**
 * Last time here: the series' latest earlier race at this track, in figures — the race's length,
 * its cautions and the share of it run under yellow, the class's green and quickest lap, the
 * stops the winner made and what most cars did, who finished. Shown large while this race is
 * young (before it has shown a stint length of its own), then as a single tile and a row.
 */
export function LastHerePanel({ lastHere, cls }: { lastHere: LastHere; cls: string }) {
  const r = lastHere.race;
  const c = r.byClass[cls];
  const g = lastHere.greenLapS[cls];
  const yellowShare =
    r.durationS && r.durationS > 0 ? Math.min(1, r.cautionS / r.durationS) : null;
  const stat = (big: ReactNode, sub?: ReactNode, color?: string) => (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div
        className="truncate text-2xl font-semibold leading-tight tabular-nums"
        style={color ? { color } : undefined}
      >
        {big}
      </div>
      {sub && (
        <div className="truncate text-xs" style={{ color: T.muted }}>
          {sub}
        </div>
      )}
    </div>
  );
  return (
    <section
      className="flex flex-col gap-3 rounded-xl border px-4 py-3"
      style={{ borderColor: T.accent, background: "rgba(244,140,52,0.06)" }}
      data-testid="last-here"
    >
      <h3
        className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: T.accent }}
      >
        Last time here · {lastHere.date.slice(0, 4)}
        {lastHere.name ? (
          <span style={{ color: T.muted }}> · {lastHere.name}</span>
        ) : null}
      </h3>
      <div
        className="grid gap-x-4 gap-y-3"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(min(8.5rem, 100%), 1fr))",
        }}
      >
        {stat(
          `${r.laps} laps`,
          r.durationS ? `in ${lastHereClock(r.durationS, "span")}` : undefined
        )}
        {stat(
          r.cautions
            ? `${r.cautions} caution${r.cautions === 1 ? "" : "s"}`
            : "No cautions",
          r.cautions
            ? `${lastHereClock(r.cautionS, "span")} under yellow${yellowShare !== null ? ` (${Math.round(yellowShare * 100)} %)` : ""}`
            : undefined,
          r.cautions ? T.caution : undefined
        )}
        {g
          ? stat(
              lastHereClock(g, "lap"),
              `${cls} green lap${c?.fastestLapS ? ` · quickest ${lastHereClock(c.fastestLapS, "lap")}` : ""}`
            )
          : null}
        {c && c.winnerStops !== null
          ? stat(
              `${c.winnerStops} stop${c.winnerStops === 1 ? "" : "s"}`,
              `${cls} winner${c.stopsMedian !== null && c.stopsMedian !== c.winnerStops ? ` · most cars ${c.stopsMedian}` : " · as most cars"}`
            )
          : null}
        {c ? stat(`${c.finished} of ${c.cars}`, `${cls} cars finished`) : null}
      </div>
      {yellowShare !== null && (
        <div
          className="flex items-center gap-2 text-[11px]"
          style={{ color: T.muted }}
          aria-label={`${Math.round(yellowShare * 100)} % of the race under yellow`}
        >
          <div
            className="h-1.5 flex-1 overflow-hidden rounded-full"
            style={{ background: T.gain }}
          >
            <div
              className="h-full"
              style={{
                width: `${Math.round(yellowShare * 100)}%`,
                background: T.caution,
                marginLeft: "auto",
              }}
            />
          </div>
          <span className="tabular-nums">
            {Math.round((1 - yellowShare) * 100)} % green
          </span>
        </div>
      )}
    </section>
  );
}

/**
 * Whether the strategy proper is on the page for the focus car — a pit window or a projected
 * finish rather than "Not known yet". The "last time here" panel stands in until it is, then
 * shrinks to a tile and a row.
 */
const strategyKnown = (s: LiveStrategy) =>
  !!s.focus && (s.focus.car.stopsToGo !== null || s.focus.car.projPos !== null);

/** A headline in the strip across the top: what it is, the number, one line under it. */
function Tile({ kicker, big, sub, color, tone }: { kicker: string; big: ReactNode; sub?: ReactNode; color?: string; tone?: "caution" }) {
  const c = tone === "caution" ? T.caution : (color ?? T.muted);
  return (
    <section
      className="flex min-w-0 flex-col gap-0.5 rounded-lg border px-3 py-2"
      style={{ borderColor: tone === "caution" || color ? c : T.line, background: tone === "caution" ? "rgba(250,204,21,0.08)" : T.card }}
    >
      <h3 className="m-0 truncate text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: c }}>
        {kicker}
      </h3>
      <div className="truncate text-xl font-semibold leading-tight tabular-nums">{big}</div>
      {sub && (
        <div className="truncate text-xs" style={{ color: T.soft }}>
          {sub}
        </div>
      )}
    </section>
  );
}

/** One line of the detail under the strip: its label, then the line (wrapping only when narrow). */
function Row({ label, color, children }: { label: string; color?: string; children: ReactNode }) {
  return (
    <div className="grid gap-x-4 gap-y-0.5 border-t px-3 py-1.5 sm:grid-cols-[9rem_minmax(0,1fr)]" style={{ borderColor: T.line }}>
      <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] sm:pt-0.5" style={{ color: color ?? T.muted }}>
        {label}
      </dt>
      <dd className="m-0 text-[13px] leading-snug" style={{ color: T.soft }}>
        {children}
      </dd>
    </div>
  );
}

const join = (xs: (string | null | false | undefined)[]) => xs.filter(Boolean).join(" · ");
const strip = (t: string) => t.replace(/\.$/, "");

/**
 * The focus car's strategy: a strip of headlines across the top (the battle, the pit window, a
 * stop now, the cautions, the finish, in that order), then the detail as one line each under it.
 * Nothing in columns of uneven height, so no empty space.
 */
function StrategySheet({ s, data, cautionRates, battles, fuelLapS, lastHere }: { s: LiveStrategy; data: RaceChartData; cautionRates?: CautionRates; battles: Battle[]; fuelLapS: number | null; lastHere?: LastHere | null }) {
  const f = s.focus!;
  const c = f.car;
  const name = (n: number) => `#${carNo(data, n)}`;
  const likely = likelyText(c);
  const edge = stopsEdge(c);
  const close = closeCall(s, c);
  const reach = !c.stopped ? reachToFlag(c) : null;
  const range = finishRange(s, c, data, finishRangeBandsFor(cautionRates));
  const cautions = cautionsCard(s);
  const pace = s.cars[0]?.pace ?? null;
  const stint = strip(
    `${c.lastStopLap > 0 ? `${c.stintLaps} laps since its stop on lap ${c.lastStopLap}` : `${c.stintLaps} laps, no stop yet`}${cautionText(c)}${s.stintLength !== null ? ` · ${s.cls} ~${s.stintLength} green laps a tank${s.stintLengthFrom === "history" ? " (from earlier races)" : ""}` : ""}`
  );

  // The strip.
  const tiles: ReactNode[] = [];
  // Last time here: as a tile and a row once the strategy itself is on the page (the panel above the strip until then).
  const last = lastHereText(lastHere, s.cls);
  if (last && lastHere && strategyKnown(s)) {
    const r = lastHere.race;
    tiles.push(
      <Tile
        key="last"
        kicker={`Last time here · ${lastHere.date.slice(0, 4)}`}
        big={`${r.laps} laps${r.cautions ? ` · ${r.cautions} caution${r.cautions === 1 ? "" : "s"}` : ""}`}
        sub={lastHere.greenLapS[s.cls] ? `${s.cls} green lap ${lastHereClock(lastHere.greenLapS[s.cls]!, "lap")}` : lastHere.name ?? undefined}
      />
    );
  }
  const top = battles[0];
  if (top) {
    const st = BATTLE[top.kind];
    const t = battleText(top, name(top.rival));
    tiles.push(
      <Tile
        key="battle"
        kicker={`${st.label} · ${name(top.rival)} ${top.side}`}
        color={top.kind === "safe" || top.kind === "out_of_reach" ? undefined : st.color}
        big={t.target ? t.target.lap : `${gapClock(top.gapS)} ${top.side}`}
        sub={t.target ? t.target.why : t.big}
      />
    );
  }
  const windowBig = c.stopped
    ? "Stopped"
    : c.stopsToGo === null
      ? "Not known yet"
      : c.stopsToGo === 0
        ? reach && reach.save > 0
          ? `${lapsWord(reach.save)} short`
          : "Reaches the flag"
        : f.window
          ? f.window.from >= f.window.to
            ? `By L${lapAhead(f.window.to, c.laps)}`
            : `L${lapsAhead(f.window.from, f.window.to, c.laps)}`
          : "—";
  const windowSub =
    c.stopped || c.stopsToGo === null
      ? null
      : c.stopsToGo === 0
        ? reach && reach.save > 0
          ? "of fuel to the flag"
          : reach
            ? reach.spare > 0
              ? `${lapsWord(reach.spare)} to spare`
              : "no lap to spare"
            : null
        : `${stopsWord(c.stopsLikely ?? c.stopsToGo)} to go`;
  tiles.push(<Tile key="window" kicker={`Pit window · #${carNo(data, c.num)}`} tone={reach?.tight ? "caution" : undefined} big={windowBig} sub={windowSub} />);
  if (f.pitNow) {
    tiles.push(
      <Tile
        key="now"
        kicker={f.pitNow.caution ? "Pit now · caution" : "Pit now"}
        tone={f.pitNow.caution ? "caution" : undefined}
        big={f.pitNow.pitClosed ? "Pits closed" : `P${f.pitNow.rejoinPos} in class${pitTrendArrow(f.pitNow) ? ` ${pitTrendArrow(f.pitNow)}` : ""}`}
        sub={f.pitNow.pitClosed ? (f.pitNow.pitClosedShort ? "short yellow" : "not open to its class") : join([`loses ~${gapClock(f.pitNow.lostS)}`, pitTrendText(f.pitNow)])}
      />
    );
  }
  const odds = s.cautionOdds ?? null;
  const so = s.cautionsSoFar ?? { count: 0, yellowS: 0 };
  tiles.push(
    <Tile
      key="caution"
      kicker={s.caution ? "Caution · now" : "Caution chance"}
      tone={s.caution ? "caution" : undefined}
      big={odds ? odds.word.replace(/^./, (x) => x.toUpperCase()) : `${so.count} so far`}
      sub={s.caution && s.cautionSoFarS != null ? `this one ${Math.round(s.cautionSoFarS / 60)} min so far` : `${so.count} so far · ${Math.round(so.yellowS / 60)} min yellow`}
    />
  );
  const leader = s.cars.find((x) => x.projPos === 1);
  tiles.push(
    <Tile
      key="finish"
      kicker="Projected finish"
      big={c.projPos === null ? "Not known yet" : close ? `P${Math.min(c.projPos, close.other.projPos!)}–P${Math.max(c.projPos, close.other.projPos!)}` : `P${c.projPos}`}
      sub={
        c.projPos === null
          ? null
          : close
            ? "too close to call"
            : c.projPos === 1
              ? `${s.cls} winner`
              : leader && c.projGapS !== null
                ? `${gapWords(c.projGapS, pace)} behind ${name(leader.num)}`
                : null
      }
    />
  );

  // The detail, one line each.
  const rows: ReactNode[] = [];
  if (last && strategyKnown(s)) rows.push(<Row key="last" label="Last time here">{last}</Row>);
  battles.forEach((b) => {
    const st = BATTLE[b.kind];
    const t = battleText(b, name(b.rival));
    const quiet = b.kind === "safe" || b.kind === "out_of_reach";
    rows.push(
      <Row key={`b-${b.side}-${b.rival}`} label={`${st.label} · ${name(b.rival)}${b.teammate ? " (team)" : ""}`} color={quiet ? undefined : st.color}>
        <span className="font-semibold" style={{ color: T.text }}>
          {t.big}
        </span>
        {t.target && (
          <span data-testid="battle-target">
            {" · "}
            <span className="font-bold tabular-nums" style={{ color: st.color }} title="Target lap time">
              {t.target.lap}
            </span>{" "}
            {t.target.why}
            {t.target.delta ? ` (${t.target.delta})` : ""}
          </span>
        )}
        {` · ${strip(t.note)}`}
        {fuelLapS !== null && b.targetS !== null && b.targetS < fuelLapS - 0.05 && (
          <span style={{ color: T.caution }}>{` · quicker than the ${lapClock(fuelLapS)} its fuel needs to reach the flag: that pace means a splash`}</span>
        )}
      </Row>
    );
  });
  if (c.stopped) rows.push(<Row key="w" label="Pit window">Stopped.</Row>);
  else if (c.stopsToGo === null)
    rows.push(
      <Row key="w" label="Pit window">
        {s.stintLength === null ? `Waits for the ${s.cls} cars to run a few full stints.` : "Waits for the car's green-flag pace and the time to go."}
      </Row>
    );
  else if (c.stopsToGo === 0 && reach) {
    rows.push(
      <Row key="w" label="Pit window" color={reach.tight ? T.caution : undefined}>
        {join([
          reach.save > 0 ? `About ${lapsWord(reach.save)} of fuel short of the flag` : `Can reach the flag, ${reach.spare > 0 ? `${lapsWord(reach.spare)} to spare` : "no lap to spare"}`,
          `${c.lastStopLap > 0 ? `${c.stintLaps} laps since its stop on lap ${c.lastStopLap}` : `${c.stintLaps} laps without a stop`}${cautionText(c)} + ${reach.toFlag} to the flag if green = ${lapsWord(reach.stint)}' fuel`,
          strip(capText(s, c)),
        ])}
      </Row>
    );
    if (reach.save > 0)
      rows.push(
        <Row key="fuel" label="To the flag" color={T.caution}>
          <FuelOptions s={s} c={c} reach={reach} data={data} />
        </Row>
      );
  } else if (c.stopsToGo === 0) {
    rows.push(<Row key="w" label="Pit window">{join(["Can reach the flag", stint, likely && strip(likely)])}</Row>);
  } else if (f.window) {
    rows.push(
      <Row key="w" label="Pit window">
        <span className="font-semibold" style={{ color: T.text }}>
          {f.window.from >= f.window.to ? `By lap ${lapAhead(f.window.to, c.laps)}` : `Laps ${lapsAhead(f.window.from, f.window.to, c.laps)}`}
        </span>
        {` · ${join([
          f.window.stopsToGo === 1 ? `one stop to go${likely ? " on fuel" : ""}` : `${f.window.stopsToGo} stops to go${likely ? " on fuel" : ""}; stopping before lap ${lapAhead(f.window.from, c.laps)} adds one`,
          stint,
        ])}`}
        {likely && <span className="block font-semibold">{likely}</span>}
      </Row>
    );
  }
  if (edge)
    rows.push(
      <Row key="edge" label="On the edge" color={T.caution}>
        It needs {lapsWord(edge.need)} of fuel to the flag
        {edge.fewer ? `, and ${givesText(edge.stops - 1, edge.fewer.gives)}: with a lap fewer to go, ${stopsWord(edge.stops - 1)} would do` : ""}
        {edge.more ? `${edge.fewer ? ";" : ","} ${givesText(edge.stops, edge.more.gives)}: with a lap more to go, it would need ${stopsWord(edge.stops + 1)}` : ""} (laps to go estimated).
      </Row>
    );
  if (f.pitNow)
    rows.push(
      <Row key="now" label={f.pitNow.caution ? "Pit now · caution" : "Pit now"} color={f.pitNow.caution ? T.caution : undefined}>
        {f.pitNow.pitClosed
          ? pitClosedText(f.pitNow)
          : join([
              `Would rejoin P${f.pitNow.rejoinPos} in class`,
              `loses ~${gapClock(f.pitNow.lostS)}${f.pitNow.savesS !== null && f.pitNow.savesS > 0 ? `, ${gapClock(f.pitNow.savesS)} less than green` : ""}${f.pitNow.measured ? " (this race's stops)" : " (estimated)"}`,
              f.pitNow.caution && "allows for cars behind stopping too",
              !!f.pitNow.later?.length && `in a lap P${f.pitNow.later[0].rejoinPos ?? "–"}, in two P${f.pitNow.later[1]?.rejoinPos ?? "–"}${f.pitNow.trend ? ` · ${pitTrendText(f.pitNow)}` : ""}`,
              f.pitNow.settledPos !== null && f.pitNow.settledPos !== f.pitNow.rejoinPos && strip(settledText(f.pitNow, name) ?? ""),
            ])}
      </Row>
    );
  const length = s.caution ? cautionLengthText(s) : null;
  if (length) rows.push(<Row key="len" label="This caution" color={T.caution}>{strip(length.replace(/^This caution: /, ""))}</Row>);
  rows.push(
    <Row key="cau" label="Cautions">
      {join(cautions.notes.filter((n) => n !== length).map(strip))}
    </Row>
  );
  if (c.projPos === null) rows.push(<Row key="fin" label="Finish">Needs the car's stops to go and its green-flag pace.</Row>);
  else {
    rows.push(
      <Row key="fin" label="Finish">
        {join([
          strip(projectedNote(s, data)),
          close && `${gapClock(close.gapS)} between it and ${name(close.other.num)} with ${lapsWord(c.lapsLeft ?? 0)} to go: pace, a stop or traffic could swap them`,
        ])}
      </Row>
    );
    if (range) {
      const [likelyLine, ...rest] = rangeLines(range, name);
      rows.push(<Row key="rng" label="Likely">{strip(likelyLine.replace(/^Likely /, ""))}</Row>);
      rest.forEach((t, i) => {
        const m = t.match(/^(For P\d+|To keep #\S+ behind): (.*)$/);
        rows.push(
          <Row key={`r${i}`} label={m ? (m[1].startsWith("For") ? m[1] : "Keep behind") : "Finish"}>
            {strip(m ? (m[1].startsWith("For") ? m[2] : `${m[1].replace(/^To keep (\S+) behind$/, "$1")}: ${m[2]}`) : t)}
          </Row>
        );
      });
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(10.5rem, 100%), 1fr))" }} data-testid="strategy-strip">
        {tiles}
      </div>
      <dl className="m-0 overflow-hidden rounded-lg border [&>div:first-child]:border-t-0" style={{ borderColor: T.line, background: T.card }} data-testid="strategy-rows">
        {rows}
      </dl>
    </div>
  );
}

/**
 * The finishing range under the projected place: "Likely P3–P6 (8 in 10 finishes from here land in
 * the range)", then what the better end takes and what keeps the worse one away. What it takes, not
 * advice.
 */
export function rangeLines(range: FinishRange, name: (num: number) => string): string[] {
  const out: string[] = [];
  out.push(
    range.best === range.worst
      ? `Likely P${range.best} (8 in 10 finishes from here, on the series' 2026 races).`
      : `Likely P${range.best}–P${range.worst} (8 in 10 finishes from here, on the series' 2026 races).`
  );
  for (const o of range.ahead) {
    const pace =
      o.targetS !== null
        ? `${o.perLapS.toFixed(1)} s/lap quicker (${lapClock(o.targetS)}, run lately)`
        : o.beyondPace
          ? `${o.perLapS.toFixed(1)} s/lap quicker (more than it has run lately)`
          : `${o.perLapS.toFixed(1)} s/lap quicker`;
    out.push(`For P${o.projPos}: ${name(o.num)} is ${gapClock(o.gapS)} ahead at the flag · ${pace}${o.cautionStopCloses ? " · or a stop under a caution" : ""}.`);
  }
  for (const o of range.behind) {
    out.push(`To keep ${name(o.num)} behind: ${gapClock(o.gapS)} at the flag · ${o.perLapS.toFixed(1)} s/lap in hand.`);
  }
  return out;
}

/** "1 h 41 min", "52 min". */
function hoursWords(h: number): string {
  const m = Math.round(h * 60);
  return m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}` : `${m} min`;
}

/** The series' name for the caution rates in use, for the Cautions card. */
function seriesName(r: CautionRates | undefined): string | null {
  return r === WRL_CAUTION_RATES ? "WRL" : r === WEATHERTECH_CAUTION_RATES ? "WeatherTech" : r === PILOT_CHALLENGE_CAUTION_RATES ? "Pilot Challenge" : null;
}

/**
 * The Cautions card: the chance of another caution before the flag (cautionChance) in words, the
 * caution under way (cautionLengthText), the race's cautions so far against the series', and what a
 * stop under one saves. A chance, not a forecast of when. On a phone, one line (line).
 */
export function cautionsCard(s: Pick<LiveStrategy, "caution" | "cautionOdds" | "cautionsSoFar" | "cautionLengths" | "cautionSoFarS" | "greenStopS" | "cautionStopS" | "cautionMeasured">): { big: string; notes: string[]; line: string } {
  const odds = s.cautionOdds ?? null;
  const so = s.cautionsSoFar ?? { count: 0, yellowS: 0 };
  const another = s.caution ? "Another caution" : "A caution";
  const big = odds ? `${another} before the flag: ${odds.word}` : s.caution ? "Caution" : so.count ? `${so.count} so far` : "None so far";
  const notes: string[] = [];
  const length = s.caution ? cautionLengthText(s) : null;
  if (length) notes.push(length);
  const race = so.count ? `This race: ${so.count} caution${so.count === 1 ? "" : "s"}, ${Math.round(so.yellowS / 60)} min under yellow.` : "This race: none yet.";
  const name = seriesName(s.cautionLengths);
  const r = s.cautionLengths;
  const q = r?.lengthPercentilesMin;
  notes.push(race);
  if (name && r?.greenHoursPerCaution) notes.push(`${name} 2026: one every ~${hoursWords(r.greenHoursPerCaution)} of green${q?.length === 19 ? `, ~${Math.round(q[9])} min long` : ""}.`);
  if (s.greenStopS !== null && s.cautionStopS !== null && s.greenStopS > s.cautionStopS) {
    notes.push(`A stop under one loses ~${gapClock(s.cautionStopS)}${s.cautionMeasured ? "" : " (estimated)"}, against ${gapClock(s.greenStopS)} under green.`);
  }
  if (odds) notes.push("A chance from the series' races, not a when.");
  const lineOdds = odds ? `${another} before the flag: ${odds.word}.` : "";
  const soFar = so.count ? `${so.count} so far, ${Math.round(so.yellowS / 60)} min under yellow` : "none yet";
  const line = s.caution && length ? `${length} ${lineOdds}`.trim() : lineOdds ? `${lineOdds.replace(/\.$/, "")} · ${soFar}.` : `Cautions: ${soFar}.`;
  return { big, notes, line };
}

/** A gap in words with its unit: "1.1 s", "12.3 s (+1L)". */
function gapWords(sec: number, pace: number | null): string {
  const t = pace ? lapsAndSeconds(sec / pace, pace) : gapClock(sec);
  return /\bs\b/.test(t) ? t : t.replace(/^([\d.]+)/, "$1 s");
}

/**
 * The focus car's projected finish in words, naming the car it is measured against: "1.1 s behind
 * #119, projected to win GTO." or "Projected to win GTO, 1.1 s ahead of #120.", then its place once
 * every car has made the stops it owes.
 */
/**
 * "This caution: 6 min so far. Cautions in this series usually last about 9 min, 9 in 10 under 17;
 * of those that run 6 min, half are over within 4 more." Null on a green track or without the
 * series' lengths.
 */
export function cautionLengthText(s: Pick<LiveStrategy, "cautionSoFarS" | "cautionLengths">): string | null {
  if (s.cautionSoFarS == null || !s.cautionLengths) return null;
  const soFar = Math.round(s.cautionSoFarS / 60);
  const n = cautionLengthNote(s.cautionLengths, s.cautionSoFarS / 60);
  if (!n) return null;
  const more = n.halfMoreMin !== null && soFar >= 1 ? ` · half this long are over within ${n.halfMoreMin} more` : "";
  return `This caution: ${soFar} min so far · usually ~${n.medianMin} min, 9 in 10 under ${n.p90Min}${more}.`;
}

/** Why the pit lane is closed to the focus car's class (PitNow.pitClosed). */
export function pitClosedText(p: Pick<PitNow, "pitClosedShort" | "pitClosedFromRc">): string {
  if (p.pitClosedShort) return `A short full-course yellow${p.pitClosedFromRc ? ", race control says" : ""}: the pit lane stays closed until the field comes to the restart, then opens for every class.`;
  return "Race control has not opened the pit lane to this class yet: it opens for the prototypes first, then the GT cars, then all.";
}

export function projectedNote(s: LiveStrategy, data: RaceChartData): string {
  const c = s.focus!.car;
  const pace = s.cars[0]?.pace ?? null;
  const winner = s.cars.find((x) => x.projPos === 1);
  const second = s.cars.find((x) => x.projPos === 2);
  let first: string;
  if (c.projPos === 1) {
    first = second && second.projGapS !== null ? `Projected ${s.cls} winner, ${gapWords(second.projGapS, pace)} ahead of #${carNo(data, second.num)}` : `Projected ${s.cls} winner`;
  } else {
    first =
      winner && c.projGapS !== null
        ? `${gapWords(c.projGapS, pace)} behind #${carNo(data, winner.num)}, the projected ${s.cls} winner`
        : `P${c.projPos} in ${s.cls} at the flag`;
  }
  return c.netPos !== null ? `${first} · P${c.netPos} after every car's stops.` : `${first}.`;
}

/** A lap time: "2:19.2". */
const lapClock = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, "0")}`;
const perLap = (s: number) => `${Math.abs(s).toFixed(1)} s/lap`;
/** "a" or "an" before a number said aloud: an 8-, 11-, 18- or 80-something-lap stint. */
/** "1 lap", "9 laps", "1.5 laps" (fuel in green laps can be a half). */
const lapsWord = (n: number) => `${Number.isInteger(n) ? n : n.toFixed(1)} lap${n === 1 ? "" : "s"}`;
/** "one stop gives up to 58", "its tank runs to 30, saving what it can" (fuel from its last stop, in green laps). */
const givesText = (stops: number, gives: number) =>
  stops <= 0 ? `its tank runs to ${gives}, saving what it can` : `${stopsWord(stops)} give${stops === 1 ? "s" : ""} up to ${gives}`;
/** "no stop", "one stop", "2 stops". */
const stopsWord = (n: number) => (n <= 0 ? "no stop" : n === 1 ? "one stop" : `${n} stops`);

/**
 * The stops it will most likely make with the cautions still to come (stopsLikely), and how often
 * that came up, when not the count its fuel needs: a caution usually brings a stop before the fuel
 * needs one (more), or the caution laps to come save fuel (fewer). Null when they are the same.
 */
export function likelyText(c: CarStrategy): string | null {
  if (c.stopped || c.stopsLikely === null || c.stopsToGo === null || c.stopsLikely === c.stopsToGo) return null;
  const share = Math.round(100 * (c.stopsChance?.get(c.stopsLikely) ?? 0));
  const n = c.stopsLikely === 0 ? "No stop" : c.stopsLikely === 1 ? "One stop" : `${c.stopsLikely} stops`;
  return c.stopsLikely > c.stopsToGo
    ? `${n} likely with the cautions to come (${share}%): a caution usually brings a stop before the fuel needs one.`
    : `${n} likely with the cautions to come (${share}%): caution laps save fuel.`;
}

/** " (4 under caution: 20 laps' fuel)": a caution lap uses CAUTION_FUEL of a green lap's fuel. */
function cautionText(c: CarStrategy): string {
  if (!c.stintCautionLaps) return "";
  return ` (${c.stintCautionLaps} under caution: ${lapsWord(c.stintFuel)}' fuel)`;
}

/** "P2 at the flag · 15.7 s behind #120" (or ahead of the car behind, for the leader). */
function atFlagText(a: AtFlag, name: (n: number) => string): string {
  const rel = a.ahead ? ` · ${gapClock(a.ahead.gapS)} behind ${name(a.ahead.num)}` : a.behind ? ` · ${gapClock(a.behind.gapS)} ahead of ${name(a.behind.num)}` : "";
  return `P${a.pos} at the flag${rel}`;
}

/**
 * Short of fuel to the flag: the two ways there side by side, each with its lap, its time lost
 * and where it finishes (fuelSaveOutcome: the class projection with each car's owed stops and
 * paces), and which comes out ahead. Saving fuel is one option, not the answer: here it can lose
 * a place a splash would too, or one a splash would not.
 */
function FuelOptions({ s, c, reach, data }: { s: LiveStrategy; c: CarStrategy; reach: ReachToFlag; data: RaceChartData }) {
  const o = fuelSaveOutcome(s, c);
  const name = (n: number) => `#${carNo(data, n)}`;
  const base = `${reach.baseFromStint ? "its last stint's pace" : "its pace"} of ${lapClock(reach.baseS)}`;
  // Which comes out ahead: the better place, else the less time lost.
  const verdict = !o
    ? null
    : o.save.pos < o.splash.pos
      ? `Saving finishes P${o.save.pos}, a splash P${o.splash.pos}.`
      : o.splash.pos < o.save.pos
        ? `A splash finishes P${o.splash.pos}, saving P${o.save.pos}: racing to the flag and splashing comes out ahead.`
        : reach.costS !== null
          ? `Either way P${o.save.pos}: saving loses about ${gapClock(Math.abs(o.splashS - reach.costS))} ${o.splashS >= reach.costS ? "less" : "more"} than a splash.`
          : null;
  const box = "flex min-w-0 flex-col gap-0.5 rounded-md border px-3 py-2";
  return (
    <>
      <div className="my-1 grid items-start gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(11rem, 100%), 1fr))" }} data-testid="fuel-options">
        <div className={box} style={{ borderColor: T.line }}>
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: T.muted }}>
            Save fuel{c.lastStopLap > 0 ? `, from lap ${c.lastStopLap}` : ""}
          </span>
          <span className="text-2xl font-bold leading-tight tabular-nums" style={{ color: T.caution }} title="Fuel-save lap time">
            {reach.targetS !== null ? lapClock(reach.targetS) : "—"}
          </span>
          {reach.targetS !== null && (
            <span className="text-xs" style={{ color: T.muted }}>
              {perLap(reach.targetS - reach.baseS)} slower than {base} · {Math.round(reach.saveShare * 100)}% less fuel a lap
            </span>
          )}
          {reach.costS !== null && <Note>~{gapClock(reach.costS)} lost over the {lapsWord(reach.toFlag)} left</Note>}
          {o && <span className="text-sm font-semibold">{atFlagText(o.save, name)}</span>}
        </div>
        {o && (
          <div className={box} style={{ borderColor: T.line }}>
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: T.muted }}>
              Race to the flag and splash
            </span>
            <span className="text-2xl font-bold leading-tight tabular-nums" title="Its lap time racing to the flag">
              {lapClock(reach.baseS)}
            </span>
            <span className="text-xs" style={{ color: T.muted }}>
              {base} · a splash of ~{lapsWord(o.splashLaps)}&apos; fuel
            </span>
            <Note>
              ~{gapClock(o.splashS)} lost{o.splashFitted ? " (as this race's splashes)" : " (estimated: no splash yet)"}
            </Note>
            <span className="text-sm font-semibold">{atFlagText(o.splash, name)}</span>
          </div>
        )}
      </div>
      {(verdict || reach.fromNow || o?.hold) && (
          <>
            {verdict && <span className="text-sm font-semibold">{verdict}</span>}
            {reach.fromNow && (
              // Timing can not show fuel saved: what saving takes if the car has not been at it.
              <Note>
                Not saving since lap {c.lastStopLap || 1}? {Math.round(reach.fromNow.share * 100)}% a lap from now
                {reach.fromNow.ok && reach.fromNow.targetS !== null ? `, ${lapClock(reach.fromNow.targetS)}.` : ": more than a car can save, so a splash."}
              </Note>
            )}
            {o?.hold && (
              <Note>
                Saving, it can lap as slow as {lapClock(o.hold.lapS)} before {name(o.hold.num)}
                {o.hold.owesStop ? " (after the stop it owes)" : ""} finishes ahead.
              </Note>
            )}
          </>
      )}
    </>
  );
}

/** "GTO cars run about 27 green laps on a tank; its longest stint so far is 28." (what its stint cap comes from). */
function capText(s: LiveStrategy, c: CarStrategy): string {
  const cls = s.stintLength !== null ? `${s.cls} ~${s.stintLength} green laps a tank${s.stintLengthFrom === "history" ? " (from earlier races)" : ""}` : null;
  const own = c.longestStint > 0 ? `its longest stint ${Math.round(c.longestStint * 10) / 10}` : null;
  return `${[cls, own].filter(Boolean).join("; ")}.`.replace(/^./, (x) => x.toUpperCase());
}

const BATTLE: Record<Battle["kind"], { label: string; color: string; bg: string }> = {
  push: { label: "Push", color: T.gain, bg: "rgba(74,222,128,0.08)" },
  push_harder: { label: "Push harder", color: T.accent, bg: "rgba(244,140,52,0.08)" },
  out_of_reach: { label: "Out of reach", color: T.muted, bg: T.card },
  defend: { label: "Defend", color: T.lose, bg: "rgba(248,113,113,0.08)" },
  safe: { label: "Safe", color: T.muted, bg: T.card },
  passes_in_pits: { label: "Gains in the pits", color: T.gain, bg: T.card },
  net_rival: { label: "Net rival", color: T.lose, bg: "rgba(248,113,113,0.06)" },
};

/** The lap time a battle needs, shown large in its card: the time and what it does. */
export interface BattleTarget {
  /** "2:18.6" */
  lap: string;
  /** "or faster to finish ahead" */
  why: string;
  /** "0.8 s/lap quicker than now", when the car isn't already on it. */
  delta: string | null;
}

/**
 * One battle in words: the headline, the target lap (when there is one) and the line under it. Every headline names the rival and reads from the focus car's side ("Behind
 * #120 after the stops", "#678 on your tail by lap 153"), as the notes do: a bare "Ahead after the
 * stops" on a card about another car read as the focus car's.
 */
export function battleText(b: Battle, num: string): { big: string; note: string; target: BattleTarget | null } {
  const gap = gapClock(b.gapS);
  const until = `${b.until} (${lapsWord(b.horizon)})`;
  const target = (why: string): BattleTarget | null =>
    b.targetS === null
      ? null
      : {
          lap: lapClock(b.targetS),
          why,
          delta: b.paceS !== null && b.paceS - b.targetS >= 0.05 ? `${perLap(b.paceS - b.targetS)} quicker than now` : null,
        };
  switch (b.kind) {
    case "push":
      return {
        big: `On ${num}'s tail by lap ${b.onLap} (${b.inLaps})`,
        note: `Catching ${num} by ${perLap(b.closingS!)}: ${lapsWord(b.inLaps!)}, ${lapsWord(Math.max(0, b.horizon - b.inLaps!))} before ${b.until}.`,
        target: target("or faster to keep catching"),
      };
    case "push_harder":
      return {
        big: `Catch ${num} before ${b.until}`,
        note: `${num} is ${gap} ahead, with ${lapsWord(b.horizon)} until ${b.until}.`,
        target: target(`or faster to catch it before ${b.until}`),
      };
    case "out_of_reach":
      return {
        big: `${num} out of reach this stint`,
        note: `${num} is ${gap} ahead${b.closingS !== null ? (b.closingS > 0 ? `, catching by ${perLap(b.closingS)}` : `, pulling away by ${perLap(b.closingS)}`) : ""}: catching it before ${until} takes more pace than you've run lately.`,
        target: null,
      };
    case "defend":
      return {
        big: `${num} on your tail by lap ${b.onLap} (${b.inLaps})`,
        note: `${num} is ${gap} behind and closing at ${perLap(b.closingS!)}: on your tail in ${lapsWord(b.inLaps!)}.`,
        target: target(`or faster to stay clear until ${until}`),
        ...(b.beyondPace ? { note: `${num} is ${gap} behind and closing at ${perLap(b.closingS!)}: on your tail in ${lapsWord(b.inLaps!)}. Staying clear until ${until} takes more pace than you've run lately.` } : {}),
      };
    case "safe":
      return {
        big: `Safe from ${num}`,
        note: `${num} is ${gap} behind${b.closingS !== null ? (b.closingS > 0 ? `, closing at ${perLap(b.closingS)}, but not before ${until}` : `, ${perLap(b.closingS)} slower`) : ""}.`,
        target: null,
      };
    case "passes_in_pits":
      return {
        big: `Ahead of ${num} once it stops`,
        note: `${num} has a stop more to make: you're ahead once it stops${b.netS !== null ? `, by ${gapClock(b.netS)}` : ""}. No need to fight it on track.`,
        target: null,
      };
    case "net_rival":
      return {
        big: `Behind ${num} after the stops`,
        note: `${num} has a stop fewer: it's ahead once both have stopped${b.netS !== null ? `, by ${gapClock(-b.netS)}` : ""}.${
          b.targetS !== null ? " Gain that back by the flag." : b.beyondPace ? " Gaining that back by the flag takes more pace than you've run lately." : ""
        }`,
        target: target("or faster to finish ahead"),
      };
  }
}

function ClassTable({ s, data, focusNum, setFocusNum }: { s: LiveStrategy; data: RaceChartData; focusNum: number; setFocusNum: (n: number) => void }) {
  const leadPace = s.cars[0].pace;
  const fcyPace = new Map(s.cars.map((c) => [c.num, recentPace(data, c.num).now]));
  const focusPace = fcyPace.get(focusNum) ?? null;
  const ld = lapsDown(data);
  // Moves are counted among the cars still running (a stopped car ahead is not a place gained).
  const runningRank = new Map(s.cars.filter((c) => c.netPos !== null).map((c, i) => [c.num, i + 1]));
  const head = (label: string, title: string, right = true) => (
    <th scope="col" className={`whitespace-nowrap px-2 py-1.5 font-semibold ${right ? "text-right" : "text-left"}`} title={title}>
      {label}
    </th>
  );
  const row = (c: CarStrategy) => {
    const focus = c.num === focusNum;
    return (
      <tr
        key={c.num}
        onClick={() => setFocusNum(c.num)}
        className="cursor-pointer border-t hover:brightness-125"
        style={{ borderColor: T.line, background: focus ? "rgba(244,140,52,0.12)" : undefined }}
      >
        <td className="px-2 py-1.5 text-right">P{c.posClass}</td>
        <td className="px-2 py-1.5">
          <span className="font-mono font-semibold" style={{ color: focus ? T.accent : T.text }}>
            #{carNo(data, c.num)}
          </span>{" "}
          <span style={{ color: T.muted }}>{c.team}</span>
        </td>
        <td className="px-2 py-1.5 text-right">{c.laps}</td>
        <td className="px-2 py-1.5 text-right tabular-nums" style={{ color: (ld.get(c.num) ?? 0) > 0 ? T.text : T.muted }}>
          {ld.get(c.num) ?? "—"}
        </td>
        <td className="px-2 py-1.5 text-right">{c.stopped ? "—" : behind(c.gapS, leadPace)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">
          {(() => {
            const p = fcyPace.get(c.num) ?? null;
            if (focus || c.stopped || p === null || focusPace === null) return <span style={{ color: T.muted }}>—</span>;
            const d = p - focusPace;
            return <span style={{ color: Math.abs(d) < 0.05 ? T.muted : d < 0 ? T.lose : T.gain }}>{`${d < 0 ? "−" : "+"}${Math.abs(d).toFixed(1)}`}</span>;
          })()}
        </td>
        <td className="px-2 py-1.5 text-right">{c.stops.length}</td>
        <td className="px-2 py-1.5 text-right">{c.stopped ? "—" : c.stintLaps}</td>
        <td className="px-2 py-1.5 text-right">{c.stopped ? <span style={{ color: T.muted }}>Stopped</span> : (c.stopsLikely ?? c.stopsToGo ?? "—")}</td>
        <td className="px-2 py-1.5 text-right">
          <Move from={runningRank.get(c.num) ?? c.posClass} to={c.netPos} />
        </td>
        <td className="px-2 py-1.5 text-right">
          <Move from={runningRank.get(c.num) ?? c.posClass} to={c.projPos} />
        </td>
        <td className="hidden px-2 py-1.5 text-right md:table-cell">{c.projGapS === null ? "—" : behind(c.projGapS, leadPace)}</td>
      </tr>
    );
  };
  return (
    // Sideways on a phone: the full table, scrolling sideways inside its own box.
    <div data-wide-table className="custom-scroll overflow-x-auto rounded-lg border" style={{ borderColor: T.line }}>
      <table className="w-full min-w-[640px] text-sm" style={{ color: T.text }}>
        <caption className="px-3 pb-1 pt-2.5 text-left text-xs" style={{ color: T.muted }}>
          {s.cls}: tap a car to follow it.
        </caption>
        <thead style={{ color: T.muted }}>
          <tr>
            {head("Now", "Class position now")}
            {head("Car", "The car and its team. Tap a row to follow it.", false)}
            {head("Laps", "Laps completed")}
            {head("LD", "Laps down to the class leader, counted when the car last crossed the line (0: on the lead lap)")}
            {head("Behind", "Behind the class leader now (whole laps at the leader's pace, then seconds)")}
            {head("Δ pace", "Its pace over its last 5 green laps against the focus car's (s/lap): − quicker than the focus car, + slower")}
            {head("Stops", "Stops made: every pit-lane visit, as on every page")}
            {head("Stint", "Laps since its last stop")}
            {head("To go", "Stops still needed to reach the flag, from the class's long stints")}
            {head("After stops", "Class position once every car has made the stops it owes (green-flag stops)")}
            {head("Projected", "Class position at the flag: after the stops, at each car's green-flag pace (far from the flag, only part of what those would change)")}
            <th scope="col" className="hidden whitespace-nowrap px-2 py-1.5 text-right font-semibold md:table-cell" title="Behind the class's first at the flag, projected">
              At the flag
            </th>
          </tr>
        </thead>
        <tbody>{s.cars.map(row)}</tbody>
      </table>
    </div>
  );
}

export function LiveStrategyTab({
  data,
  annotations,
  focusNum,
  setFocusNum,
  flag,
  timeToGoS,
  cautionRates,
  history = null,
  lastHere = null,
}: {
  data: RaceChartData;
  annotations: AnnotationData | null;
  focusNum: number;
  setFocusNum: (n: number) => void;
  flag: string | null;
  timeToGoS: number | null;
  /** The race's series' caution rates (cautionRatesFor); WRL's when not given. */
  cautionRates?: CautionRates;
  /** What the series' earlier races say (GET /api/live/:slug/history); none until loaded. */
  history?: RaceHistory | null;
  /** The series' latest earlier race at this track, in figures (the same response's `lastHere`). */
  lastHere?: LastHere | null;
}) {
  // The page re-renders each second (the race clock); the strategy only changes with the chart, the
  // focus car, the flag and the time to go, and the race's stops are worked out once per chart.
  const s = useMemo(() => liveStrategy({ data, annotations, focusNum, flag, timeToGoS, cautionRates, cautionStops: true, history }), [data, annotations, focusNum, flag, timeToGoS, cautionRates, history]);
  const battles = useMemo(() => (s?.focus ? liveBattles(s, data) : []), [s, data]);
  // Short of fuel: the lap its fuel needs, which a battle's quicker target would not leave it.
  const fuelReach = s?.focus && !s.focus.car.stopped ? reachToFlag(s.focus.car) : null;
  const fuelLap = fuelReach && fuelReach.save > 0 ? fuelReach.targetS : null;
  const compact = useIsCompact();
  const landscape = useIsLandscapePhone();
  if (!s || !s.focus) {
    // A car is picked but has no lap yet (the start, or a car yet to take the green): say so.
    const car = data.cars[String(focusNum)];
    return (
      <div className="flex flex-col gap-3" style={{ color: T.muted }}>
        {car && lastHere && <LastHerePanel lastHere={lastHere} cls={car.cls} />}
        <p className="m-0">{car && !car.laps.length ? `The strategy starts once #${carNo(data, focusNum)} has run its first lap.` : "Pick a car to see its strategy."}</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4" style={{ color: T.text }}>
      {lastHere && !strategyKnown(s) && <LastHerePanel lastHere={lastHere} cls={s.cls} />}
      <StrategySheet s={s} data={data} cautionRates={cautionRates} battles={battles} fuelLapS={fuelLap} lastHere={lastHere} />
      {compact && !landscape ? (
        <RankedClass s={s} data={data} focusNum={focusNum} setFocusNum={setFocusNum} />
      ) : (
        <ClassTable s={s} data={data} focusNum={focusNum} setFocusNum={setFocusNum} />
      )}
      <p className="m-0 max-w-3xl text-xs leading-relaxed" style={{ color: T.muted }}>
        Estimates from this race so far, updated with each lap.
        {s.greenStopS !== null ? ` A green-flag stop loses about ${gapClock(s.greenStopS)}` : ""}
        {s.cautionStopS !== null ? `, a stop under a caution about ${gapClock(s.cautionStopS)}${s.cautionMeasured ? "" : " (estimated)"}` : ""}
        {s.greenStopS !== null ? "." : ""}
{" "}
        Cautions, repairs and penalties change them.
      </p>
    </div>
  );
}

/**
 * Phones upright: the class ranked on one number at a time (RankedMetrics) in place of the
 * 12-column table; a tap follows the car, as on the table.
 */
function RankedClass({ s, data, focusNum, setFocusNum }: { s: LiveStrategy; data: RaceChartData; focusNum: number; setFocusNum: (n: number) => void }) {
  const leadPace = s.cars[0]?.pace ?? null;
  const ld = lapsDown(data);
  const pace = new Map(s.cars.map((c) => [c.num, recentPace(data, c.num).now]));
  const metrics: RankedMetric<CarStrategy>[] = [
    { key: "proj", label: "Projected", better: "low", value: (c) => c.projPos, format: (v) => `P${v}`, note: "Class position at the flag, projected" },
    { key: "net", label: "After stops", better: "low", value: (c) => c.netPos, format: (v) => `P${v}`, note: "Once every car has made the stops it owes" },
    { key: "now", label: "Now", better: "low", value: (c) => c.posClass, format: (v) => `P${v}`, note: "Class position now" },
    { key: "pace", label: "Pace", better: "low", value: (c) => (c.stopped ? null : (pace.get(c.num) ?? null)), format: lapClock, note: "Its last 5 green-flag laps" },
    { key: "behind", label: "Behind", better: "low", value: (c) => (c.stopped ? null : c.gapS), format: (v) => behind(v, leadPace), note: "Behind the class leader now" },
    { key: "toGo", label: "Stops to go", better: "low", value: (c) => (c.stopped ? null : (c.stopsLikely ?? c.stopsToGo)), format: (v) => String(v), note: "Stops it will most likely make to the flag, with the cautions to come" },
    { key: "stops", label: "Stops", better: "low", value: (c) => c.stops.length, format: (v) => String(v), note: "Stops made so far" },
  ];
  const items: RankedItem<CarStrategy>[] = s.cars.map((c) => {
    const down = ld.get(c.num) ?? 0;
    return {
      id: String(c.num),
      row: c,
      title: `#${carNo(data, c.num)} ${c.team}`,
      sub: c.stopped ? "Stopped" : `P${c.posClass} now · ${c.laps} laps${down > 0 ? ` (${down} down)` : ""} · stint ${c.stintLaps}`,
      mine: c.num === focusNum,
    };
  });
  return (
    <div className="-mx-2">
      <RankedMetrics
        items={items}
        metrics={metrics}
        title={`${s.cls}, live`}
        subtitle={`${s.cars.length} cars · tap one to follow it`}
        onOpen={(it) => setFocusNum(it.row.num)}
      />
    </div>
  );
}
